// src/utils/hap-url.utils.ts
// Utilities for constructing HAP API URLs from bootstrap node addresses

const DEFAULT_HAP_API_PORT = 10335;

/**
 * Extract the HAP HTTP API URL from a bootstrap node multiaddr.
 *
 * Bootstrap node addresses are typically in the format:
 * /dns4/dev-p2p.hap-alpha.six3ro.net/tcp/443/wss/p2p/12D3Koo...
 *
 * This extracts the hostname and constructs an HTTPS URL for the HTTP API.
 *
 * @param bootstrapNode - The bootstrap node multiaddr string
 * @param apiPort - Optional API port (default: 10335)
 * @returns The HAP HTTP API URL (e.g., https://dev-p2p.hap-alpha.six3ro.net:10335)
 */
export function getHapApiUrl(bootstrapNode: string, apiPort: number = DEFAULT_HAP_API_PORT): string | null {
  // Parse multiaddr to extract hostname
  // Examples:
  // /dns4/dev-p2p.hap-alpha.six3ro.net/tcp/443/wss/p2p/12D3Koo...
  // /ip4/1.2.3.4/tcp/9004/ws/p2p/12D3Koo...

  // Try dns4 first (most common in production)
  const dns4Match = /\/dns4\/([^/]+)/.exec(bootstrapNode);
  if (dns4Match !== null) {
    const hostname = dns4Match[1];
    // Use HTTPS for production
    return `https://${hostname}:${apiPort}`;
  }

  // Try dns6
  const dns6Match = /\/dns6\/([^/]+)/.exec(bootstrapNode);
  if (dns6Match !== null) {
    const hostname = dns6Match[1];
    return `https://${hostname}:${apiPort}`;
  }

  // Try ip4 (for development/local testing)
  const ip4Match = /\/ip4\/([^/]+)/.exec(bootstrapNode);
  if (ip4Match !== null) {
    const ip = ip4Match[1];
    // Use HTTP for local IPs (127.0.0.1, 192.168.x.x, etc.)
    const isLocal = ip.startsWith('127.') || ip.startsWith('192.168.') || ip.startsWith('10.') || ip === 'localhost';
    const protocol = isLocal ? 'http' : 'https';
    return `${protocol}://${ip}:${apiPort}`;
  }

  // Unable to parse
  return null;
}

/**
 * Check if a HAP API URL appears valid
 */
export function isValidHapApiUrl(url: string | null | undefined): url is string {
  if (url === null || url === undefined || url === '') {
    return false;
  }

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
